import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Overline, KpiCard, StatusPill } from "@/components/common";
import { TriangleAlert, Package } from "lucide-react";

export default function ProShop() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/proshop").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="h-96 animate-pulse bg-white border border-[#E5E7EB] rounded-xl" />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-[#1C1F1E]">Pro Shop</h1>
        <p className="text-[#52525B] mt-1">Retail performance and live inventory.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {d.metrics.kpis.map((k) => <KpiCard key={k.label} {...k} testid={`proshop-kpi-${k.label.toLowerCase().replace(/[^a-z]/g, "-")}`} />)}
      </div>

      {d.alerts.length > 0 && (
        <Card className="p-5 border-[#D97706]/25 bg-[#D97706]/[0.03]" data-testid="inventory-alerts">
          <div className="flex items-center gap-2 mb-3"><TriangleAlert size={16} className="text-[#D97706]" /><Overline className="!text-[#D97706]">Inventory Alerts · {d.alerts.length}</Overline></div>
          <div className="flex flex-wrap gap-2">
            {d.alerts.map((a) => (
              <div key={a.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[#E5E7EB] text-sm">
                <Package size={14} className="text-[#A1A1AA]" /><span className="text-[#1C1F1E] font-medium">{a.name}</span>
                <span className="text-[#A1A1AA]">·</span><span className="font-mono2 text-[#52525B]">{a.days_remaining}d left</span>
                {a.suggested_reorder > 0 && <><span className="text-[#A1A1AA]">·</span><span className="font-mono2 text-[#0F3F2D]">reorder +{a.suggested_reorder}</span></>}
                <StatusPill status={a.status} />
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5E7EB]"><Overline>Inventory</Overline></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#E5E7EB] text-left">
              {["Product", "Category", "On Hand", "Units Sold", "Days Left", "Suggested Reorder", "Price", "Revenue", "Status"].map((h) => <th key={h} className="px-5 py-3 overline font-semibold text-[#71717A]">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.products.map((p) => (
                <tr key={p.id} data-testid="inventory-row" className="border-b border-[#F1F3F2] last:border-0 hover:bg-[#0F3F2D]/[0.02] transition-colors">
                  <td className="px-5 py-3 font-medium text-[#1C1F1E]">{p.name}</td>
                  <td className="px-5 py-3 text-[#52525B]">{p.category}</td>
                  <td className="px-5 py-3 font-mono2 text-[#52525B]">{p.on_hand}</td>
                  <td className="px-5 py-3 font-mono2 text-[#52525B]">{p.sold}</td>
                  <td className="px-5 py-3 font-mono2 font-medium" style={{ color: p.days_remaining <= 4 ? "#DC2626" : p.days_remaining <= 8 ? "#D97706" : "#52525B" }}>{p.days_remaining}d</td>
                  <td className="px-5 py-3 font-mono2 font-semibold text-[#0F3F2D]">{p.suggested_reorder > 0 ? `+${p.suggested_reorder}` : "—"}</td>
                  <td className="px-5 py-3 font-mono2 text-[#52525B]">${p.price}</td>
                  <td className="px-5 py-3 font-mono2 font-semibold text-[#0F3F2D]">${p.revenue.toLocaleString()}</td>
                  <td className="px-5 py-3"><StatusPill status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
